import { useCallback, useMemo, useState, useRef, useEffect } from 'react';
import {
  TableOptions,
  SortingState,
  getSortedRowModel,
  getPaginationRowModel,
  ColumnFiltersState,
  VisibilityState,
  OnChangeFn,
  ColumnPinningState,
} from '@tanstack/react-table';
import { CrudFilter } from '../types';
import { DEFAULT_MIN_PAGE, getRowId } from '../utils';
import { getTableStore } from './use-table-store';

export const PAGE_SIZES = [5, 10, 20, 50, 100];

interface UseTableSettingsProps<T> {
  tableId: string;
  sorting?: boolean;
  clientSorting?: boolean;
  clientPagination?: boolean;
  minPage?: number;
  defaultPageSize?: number;
  defaultSortingState?: SortingState;
  onOriginalRowSelectionChange?: (data: T[]) => void;
  columnVisibility?: VisibilityState | null;
  columnPinning?: ColumnPinningState | null;
  onColumnVisibilityChange?: OnChangeFn<VisibilityState>;
  onColumnPinningChange?: OnChangeFn<ColumnPinningState>;
  defaultColumnPinningState?: ColumnPinningState;
  disabledColumns?: Set<string>;
}

export const pageSizeOptions = PAGE_SIZES.map(size => ({
  text: size,
  value: String(size),
}));

export const useTableSettings = <T>({
  tableId,
  sorting: withSorting = true,
  clientSorting = false,
  clientPagination = false,
  minPage = DEFAULT_MIN_PAGE,
  defaultColumnPinningState = { left: [], right: [] },
  defaultPageSize = 10,
  defaultSortingState = [],
  columnVisibility,
  columnPinning: preferencesColumnPinning,
  onColumnVisibilityChange,
  onColumnPinningChange,
  disabledColumns = new Set(),
}: UseTableSettingsProps<T>) => {
  const useTableStore = getTableStore(tableId);

  const {
    initialize,
    initialized,
    sorting,
    setSorting,
    columnPinning,
    columnFilters,
    setColumnFilters,
    pagination,
    setPagination,
    rowSelection,
    setRowSelection,
  } = useTableStore();

  const [filters, setFilters] = useState<CrudFilter[]>([]);
  const previousFiltersRef = useRef<CrudFilter[]>([]);

  const [originalRowSelection, setOriginalRowSelection] = useState<T[]>([]);

  const hasFiltersSet = filters.length > 0 || columnFilters.length > 0;

  const setFiltersWithComparison = useCallback(
    (newFilters: CrudFilter[]) => {
      const areEqual =
        JSON.stringify(newFilters) ===
        JSON.stringify(previousFiltersRef.current);

      if (!areEqual) {
        previousFiltersRef.current = newFilters;
        setFilters(newFilters);
        setPagination(p => ({ ...p, pageIndex: minPage }));
      }
    },
    [minPage, setPagination]
  );

  const onColumnFiltersChangeWithReset: OnChangeFn<ColumnFiltersState> =
    useCallback(
      updater => {
        setColumnFilters(prev =>
          typeof updater === 'function' ? updater(prev) : updater
        );
        setPagination(p => ({ ...p, pageIndex: minPage }));
      },
      [minPage, setPagination, setColumnFilters]
    );

  const onSortingChangeWithReset: OnChangeFn<SortingState> = useCallback(
    updater => {
      setSorting(prev =>
        typeof updater === 'function' ? updater(prev) : updater
      );
      setPagination(p => ({ ...p, pageIndex: minPage }));
    },
    [minPage, setPagination, setSorting]
  );

  const mergedColumnPinning = useMemo(() => {
    if (!preferencesColumnPinning) return columnPinning;

    if (
      Array.isArray(preferencesColumnPinning.left) &&
      Array.isArray(preferencesColumnPinning.right) &&
      Array.isArray(columnPinning.left) &&
      Array.isArray(columnPinning.right)
    ) {
      const left = new Set([
        ...columnPinning.left,
        ...preferencesColumnPinning.left,
      ]);
      const right = new Set([
        ...preferencesColumnPinning.right,
        ...columnPinning.right,
      ]);

      return {
        left: Array.from(left),
        right: Array.from(right),
      };
    }
  }, [columnPinning, preferencesColumnPinning]);

  const tableProps: Partial<TableOptions<T>> = useMemo(
    () => ({
      manualPagination: !clientPagination,
      onPaginationChange: pagination => {
        setPagination(pagination);
      },
      enableSorting: withSorting,
      manualSorting: withSorting && !clientSorting,
      state: {
        pagination,
        ...(withSorting && { sorting }),
        rowSelection,
        columnFilters,
        originalRowSelection,
        ...(columnVisibility && { columnVisibility }),
        ...(mergedColumnPinning && { columnPinning: mergedColumnPinning }),
      },
      getRowId,
      onRowSelectionChange: setRowSelection,
      ...(onColumnPinningChange && { onColumnPinningChange }),
      onColumnFiltersChange: onColumnFiltersChangeWithReset,
      ...(onColumnVisibilityChange && { onColumnVisibilityChange }),

      ...(clientPagination && {
        getPaginationRowModel: getPaginationRowModel<T>(),
      }),
      ...(withSorting && {
        onSortingChange: onSortingChangeWithReset,
        ...(clientSorting && { getSortedRowModel: getSortedRowModel<T>() }),
      }),
    }),
    [
      clientPagination,
      withSorting,
      clientSorting,
      pagination,
      sorting,
      rowSelection,
      columnFilters,
      mergedColumnPinning,
      originalRowSelection,
      columnVisibility,
      setRowSelection,
      onColumnPinningChange,
      onColumnFiltersChangeWithReset,
      onColumnVisibilityChange,
      onSortingChangeWithReset,
      setPagination,
    ]
  );

  const onLastElementDelete = useCallback(() => {
    if (pagination.pageIndex > 0) {
      setPagination(p => ({ ...p, pageIndex: p.pageIndex - 1 }));
    }
  }, [pagination.pageIndex, setPagination]);

  const resetPagination = useCallback(
    () => setPagination(p => ({ ...p, pageIndex: minPage })),
    [minPage, setPagination]
  );

  // const previousResetPaginationKeyRef = useRef(resetPaginationKey);
  // useEffect(() => {
  //   if (previousResetPaginationKeyRef.current !== resetPaginationKey) {
  //     previousResetPaginationKeyRef.current = resetPaginationKey;
  //     resetPagination();
  //   }
  // }, [resetPaginationKey, resetPagination]);

  useEffect(() => {
    if (initialized) return;

    initialize?.({
      sorting: defaultSortingState,
      pagination: {
        pageIndex: minPage,
        pageSize: defaultPageSize,
      },
      columnPinning: defaultColumnPinningState,
    });
  }, [
    defaultPageSize,
    minPage,
    initialize,
    initialized,
    defaultSortingState,
    defaultColumnPinningState,
  ]);

  return {
    sorting,
    tableProps,
    pageSizeOptions,
    pagination,
    minPage,
    filters,
    columnFilters,
    rowSelection,
    setRowSelection,
    hasFiltersSet,
    onLastElementDelete,
    defaultColumnPinningState,
    disabledColumns,
    onFiltersChange: setFiltersWithComparison,
    onColumnFiltersChange: onColumnFiltersChangeWithReset,
    onPaginationChange: setPagination,
    onSortingChange: onSortingChangeWithReset,
    onOriginalRowSelectionChange: setOriginalRowSelection,
    resetPagination,
  };
};
